import { TFunction } from 'i18next';
import { DropdownOption } from 'src/common/components/DropdownDS22';
import { IconIds } from 'src/common/components/IconDS22';
import {
  CAPTAIN_OPTION_1_INCLUDED_IN_PRICE,
  CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN,
  CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE,
  TIME_12,
} from 'src/common/constants';
import { Price, PriceDetails } from 'src/common/types/api/v4/boats/search';
import { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import { DurationUnit } from 'src/common/types/api/listings';
import { addDays, type Locale } from 'date-fns';
import { enUS } from 'date-fns/locale';
import newTZDate, { formatDate, todayDate } from 'src/common/utils/dateHelpers';
import type { CaptainOptions } from 'src/common/types/CaptainOptions';
import { FULL_DAY_HOURS } from 'src/listing/constants';
import { Currency } from 'src/common/types/common/Currency';
import { completePriceFormatter } from 'src/common/utils/currency';
import { DEFAULT_CURRENCY } from 'src/common/providers/CurrencyProvider';

export type PricingTier = {
  priceUnit: string;
  maxUnitQuantity: number | null;
  priceDetail: Price[];
  icon: IconIds;
};

export type PricingTierInfo = Record<string, PricingTier>;

export type UnitPriceDetail = {
  hour?: Price[];
  multiday?: Price[];
};

// TODO: don't allow undefined prices
export const getPriceDetails = (prices?: Price[]): UnitPriceDetail | undefined => {
  if (!prices) return undefined;
  return {
    hour: prices.filter((price) => price.price_unit === 'hour'),
    multiday: prices.filter((price) => price.price_unit === 'day' || price.price_unit === 'night'),
  };
};

/**
 * Return only hourly or multiday, in that order.
 */
export const getPreferredPriceTierType = (tiers: UnitPriceDetail): Price[] => {
  if (tiers?.hour?.length) {
    return tiers.hour;
  }
  if (tiers?.multiday?.length) {
    return tiers.multiday;
  }

  return [];
};

const getHeaderPrice = (prices?: Price[]): PriceDetails | undefined => {
  if (!prices) return undefined;
  if (prices.length === 1) {
    return {
      min: prices[0].value,
      max: undefined,
      unit: prices[0]?.price_unit,
    };
  }
  const min = Math.min(...prices.map((price) => price.value));
  const max = Math.max(...prices.map((price) => price.value));
  return {
    min,
    max: max === min ? undefined : max,
    unit: prices[0]?.price_unit,
  };
};

const findMinValueFn = (prev, curr) => {
  if (!prev.value) {
    return curr;
  }
  return curr.value <= prev.value ? curr : prev;
};

export const getHeaderPriceInfo = (
  unitPriceDetails?: UnitPriceDetail,
): PriceDetails | undefined => {
  if (!unitPriceDetails) return undefined;
  const minHourPrice = unitPriceDetails.hour?.reduce(findMinValueFn, {});
  const minMultidayPrice = unitPriceDetails.multiday?.reduce(findMinValueFn, {});

  const minPrice = [minHourPrice, minMultidayPrice].reduce(findMinValueFn, {});

  if (minPrice.price_unit === 'day' || minPrice.price_unit === 'night') {
    return getHeaderPrice(unitPriceDetails.multiday);
  }
  return getHeaderPrice(unitPriceDetails[minPrice.price_unit]);
};

const priceUnitIconMap = {
  hour: 'clock',
  day: 'sun',
  night: 'moon',
};

export const getPriceTierInfo = (
  unitPriceDetails?: UnitPriceDetail,
): PricingTierInfo | undefined => {
  if (!unitPriceDetails) return undefined;
  const keys = Object.keys(unitPriceDetails);
  const priceTierInfo = {};

  keys.forEach((key) => {
    if (unitPriceDetails[key] && unitPriceDetails[key].length) {
      const priceDetails: Price[] = unitPriceDetails[key];
      priceTierInfo[key] = {
        priceDetail: priceDetails,
        priceUnit: priceDetails[0].price_unit,
        icon: priceUnitIconMap[priceDetails[0].price_unit],
        maxUnitQuantity: Math.max(...priceDetails.map((price) => price.max_unit_quantity)),
      };
    }
  });

  return priceTierInfo;
};

export const getUniqueTrips = (
  instabooks?: InstabookAvailability[],
): InstabookAvailability[] => (instabooks ? instabooks.reduce((
  acc: InstabookAvailability[],
  obj: InstabookAvailability,
) => {
  if (!acc.some((o) => o.id === obj.id)) {
    acc.push(obj);
  }
  return acc;
}, []) : []);

/**
 * Must only be used for a single type of price unit e.g. 'hour', 'day', 'night'
 * Converts min/max to hours.
 */
export const getMinMaxHoursForPreferredPriceTier = (
  prices: Price[],
): { min: number; max: number } | undefined => {
  if (!prices || prices.length === 0) {
    return undefined;
  }

  const list = prices
    .slice()
    .sort((a, b) => {
      const aFixed = a.fixed_quantity ?? a.min_unit_quantity;
      const bFixed = b.fixed_quantity ?? b.min_unit_quantity;

      if (typeof aFixed !== 'number' || typeof bFixed !== 'number') {
        return 0;
      }
      return bFixed - aFixed;
    });

  if (list.length === 0) {
    return undefined;
  }

  const firstListItem = list[0];
  if (!firstListItem) {
    return undefined;
  }

  let hourMultiplier = 1;
  if (['day', 'night'].includes(list[0].price_unit)
  || ['day', 'night'].includes(list[0].fixed_unit ?? '')) {
    hourMultiplier = FULL_DAY_HOURS;
  }
  const minItem = (list[list.length - 1]);
  const maxItem = (list[0]);

  if (!minItem || !maxItem) {
    return undefined;
  }

  let min;
  let max;
  if (minItem.fixed_quantity && maxItem.fixed_quantity) {
    min = minItem.fixed_quantity;
    max = maxItem.fixed_quantity;
  } else {
    min = minItem.min_unit_quantity;
    max = maxItem.max_unit_quantity;
  }
  min *= hourMultiplier;
  max *= hourMultiplier;

  return { min, max };
};

export const formatDuration = (
  duration: number,
  { multiDay }: { multiDay: boolean },
): string => {
  if (multiDay) {
    return `PT${duration * FULL_DAY_HOURS}H`;
  }
  return `PT${duration}H`;
};

const getOption = (
  t: TFunction,
  duration: number,
  unit: DurationUnit,
  icon?: IconIds,
): DropdownOption => {
  const label = t(`trip-cart--duration-${unit}`, { duration, count: duration });
  return {
    // format of the duration => PT1H for 1 hour
    id: formatDuration(duration, { multiDay: unit !== 'hour' }),
    selectedLabel: label,
    dropdownLabel: label,
    icon,
  };
};

/**
 * @deprecated trip-cart--sequencing
 */
export const hasFullDayForSameDay = (prices: Price[]): boolean => {
  const multiDayPrices = prices.filter((price) => price.price_unit === 'day');
  if (!multiDayPrices.length) return false;

  const hourPrices = prices.filter((price) => price.price_unit === 'hour');
  if (!hourPrices.length) return false;

  return prices.some((tier) => tier.price_unit === 'day' && tier.min_unit_quantity === 1);
};

/**
 * @deprecated trip-cart--sequencing
 */
export const hasFullDayForMultiDay = (prices: Price[]): boolean => {
  if (hasFullDayForSameDay(prices)) return false;

  return prices.some((tier) => tier.price_unit === 'day' && tier.min_unit_quantity === 1);
};

/**
 * @deprecated trip-cart--sequencing
 */
export const getDurationOptions = (
  t: TFunction,
  unitPriceDetails: UnitPriceDetail | undefined,
  tierType: 'hour' | 'multiday',
  isOnlyMultiDayWithFullDay: boolean = false,
): DropdownOption[] => {
  let tiers = unitPriceDetails?.[tierType];

  // Missing data, config error
  if (!tiers) return [];

  // Manually set the tier data for 'hour' as that does not exist on unitPriceDetails
  // when pricing is only in days. We want to move '1 day' to hourly duration option
  if (tierType === 'hour' && isOnlyMultiDayWithFullDay) {
    tiers = [{
      currency: unitPriceDetails?.multiday?.[0].currency,
      min_unit_quantity: FULL_DAY_HOURS,
      max_unit_quantity: FULL_DAY_HOURS,
      price_unit: 'hour',
      value: unitPriceDetails?.multiday?.find((day) => (
        day.min_unit_quantity === 1))?.value ?? 0,
    }];
  }

  const minMaxHours = getMinMaxHoursForPreferredPriceTier(tiers);
  if (!minMaxHours) return [];

  const { min: minHours, max: maxHours } = minMaxHours;
  const hourDivisor = tierType === 'multiday' ? FULL_DAY_HOURS : 1;
  const count = (maxHours - minHours) / hourDivisor + 1;
  const min = minHours / hourDivisor;
  const unit = tiers[0].price_unit as DurationUnit;

  const options = Array.from({ length: count }, (_, i) => {
    const duration = i + min;
    return getOption(t, duration, unit);
  });

  const hasMixedPricing = unitPriceDetails && (((unitPriceDetails.hour?.length ?? 0)
    && (unitPriceDetails.multiday?.length ?? 0) > 0) || isOnlyMultiDayWithFullDay);

  if (!hasMixedPricing) {
    return options;
  }

  const allPrices = [
    ...(unitPriceDetails?.hour ?? []),
    ...(unitPriceDetails?.multiday ?? []),
  ];

  const has1Day = options[0].id === formatDuration(1, { multiDay: true });
  // Remove "1 day" option for multiday
  if (unit === 'day' && ((!hasFullDayForMultiDay(allPrices) && has1Day) || isOnlyMultiDayWithFullDay)) {
    options.shift();
  }

  // Add "Full day" option for hours
  const hasOneDay = hasFullDayForSameDay(allPrices);
  if (unit === 'hour' && hasOneDay) {
    options.push(getOption(t, 1, 'day'));
  } else if (unit === 'hour' && isOnlyMultiDayWithFullDay) {
    if (options.findIndex((option) => option.id === 'PT24H') === options.length - 1) {
      // Remove option configured from '24 hours' and then reset to 1 day
      options.pop();
    }
    // '1 Day' option is removed from multi-day array,
    // it's safe to add option to hours array
    options.push(getOption(t, 1, 'day'));
  }
  return options;
};

export const getInstabookDurationOptions = (
  t: TFunction,
  durations: number[],
): DropdownOption[] => durations
  .map((duration) => getOption(t, duration, 'hour', 'instabook-filled'));

/**
 * duration format is PT{value}
 */
export const getDurationHours = (duration: string = ''): number => {
  if (!duration) return 0;
  const indexOfHours = duration.indexOf('H');
  const indexOfPrefix = duration.indexOf('T');
  const durationHours = Number(duration.substring(indexOfPrefix + 1, indexOfHours));

  return durationHours;
};

/**
 * For hourly prices, the duration is in hours.
 * For multi-day/nights prices, the duration is in days/nights.
 */
export const getDurationDigits = (duration: string): number => {
  const durationDigits = getDurationHours(duration);

  return durationDigits >= FULL_DAY_HOURS ? durationDigits / FULL_DAY_HOURS : durationDigits;
};

const hoursForUnit = (hours: number, priceUnit: string) => {
  if (priceUnit === 'hour') return hours;
  // days and nights
  return hours * FULL_DAY_HOURS;
};

/**
 * @deprecated trip-cart--sequential
 */
export const getTripCartCaptainOptionCopy = (
  t: TFunction,
  captainOptions?: CaptainOptions,
  /** listing.captain_hourly_cost set in bridge */
  captainCost?: number,
  /** used to determine if Full Day */
  duration?: string,
  isMultiDay?: boolean,
  /** One captain option, narrowed via `CaptainOptionSegment` Toggle (or getDefaultCaptainValue) */
  selectedCaptionOption?: number,
): {
  heading: string;
  description?: string;
  tooltip?: string;
} => {
  // If included, just return that info first
  if (captainOptions && captainOptions[0] === CAPTAIN_OPTION_1_INCLUDED_IN_PRICE) {
    return {
      heading: t('trip-cart--captain-option--with-captain'),
      description: t('trip-cart--captain-option--with-captain-description'),
    };
  }
  // If 'Optional' and No Captain was selected
  if (selectedCaptionOption === CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE) {
    return {
      heading: t('trip-cart--captain-option--self-captain'),
      description: t('trip-cart--captain-option--self-captain-description'),
    };
  }

  // If Independent, more checks are needed
  if (captainOptions && captainOptions[0] === CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN) {
    // Check for No Fee OR MultiDay
    if (!captainCost || isMultiDay || getDurationHours(duration) === FULL_DAY_HOURS) {
      return {
        heading: t('trip-cart--captain-option--with-captain--separate-fee'),
        description: t('trip-cart--captain-option--with-captain--separate-fee-description'),
      };
    }

    // Otherwise, only display Captain Cost line-item
    return {
      heading: t('trip-cart--captain-option--with-captain'),
    };
  }

  // Otherwise, return for Captain Not Included
  return {
    heading: t('trip-cart--captain-option--self-captain'),
    description: t('trip-cart--captain-option--self-captain-description'),
  };
};

export const getInstabookDurationsFromTrips = (
  instabookTrips: InstabookAvailability[],
): number[] => {
  const hours = instabookTrips.map((trip) => Number(trip.trip_length.split(':')[0]));
  const durations = new Set(hours);
  return Array.from(durations).sort((a, b) => Number(a) - Number(b));
};

export const sortInstabookTripsByPrice = (
  a: InstabookAvailability,
  b: InstabookAvailability,
): number => (
  (a.transaction.amounts.renter_booking_amount ?? 0)
  - (b.transaction.amounts.renter_booking_amount ?? 0)
);

/**
 * @deprecated trip-cart--sequencing
 */
export const getInstabooksCaptainCopy = (
  t: TFunction,
  instabookTrips: InstabookAvailability[],
  duration: string,
): {
  heading: string;
  description?: string;
  tooltip?: string;
} => {
  const durationValue = getDurationHours(duration);
  const filteredTrips = instabookTrips.filter((trip) => Number(trip.trip_length.split(':')[0]) === durationValue);
  const instabookTrip = filteredTrips.sort(sortInstabookTripsByPrice)[0];
  const {
    captained,
    captain_cost_included: captainCostIncluded,
    transaction: {
      amounts: {
        captain_amount: captainAmount,
      },
    },
  } = instabookTrip;
  const captainOpts: CaptainOptions = [];
  if (captained) {
    captainOpts.push(captainCostIncluded
      ? CAPTAIN_OPTION_1_INCLUDED_IN_PRICE
      : CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN);
  } else {
    captainOpts.push(CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE);
  }
  return getTripCartCaptainOptionCopy(
    t,
    captainOpts,
    captainAmount,
    duration,
    false,
    captainOpts[0],
  );
};

export const findClosestPriceTierForDuration = (
  /** Only the preferred tier, of a single price unit type */
  preferredTier: Price[],
  duration: string,
): Price => {
  const durationHours = getDurationHours(duration) || 1;

  let price = preferredTier.find((item) => {
    const min = hoursForUnit(item.min_unit_quantity, item.price_unit);
    const max = hoursForUnit(item.max_unit_quantity, item.price_unit);
    return durationHours >= min && durationHours <= max;
  });
  if (price) {
    return price;
  }

  let minDifference = Infinity;

  preferredTier.forEach((item) => {
    const min = hoursForUnit(item.min_unit_quantity, item.price_unit);
    const max = hoursForUnit(item.max_unit_quantity, item.price_unit);
    const difference = Math.min(
      Math.abs(durationHours - min),
      Math.abs(durationHours - max),
    );
    if (difference < minDifference) {
      minDifference = difference;
      price = item;
    }
  });

  return price!;
};

export const clampDuration = (
  duration: string,
  min: number,
  max: number,
): string => {
  const durationHours = duration ? getDurationHours(duration) : 1;
  let valid = Math.max(durationHours, min);
  valid = Math.min(valid, max);
  return `PT${valid}H`;
};

export const priceIsDay = (prices: Price[]): boolean => {
  const hasDayPrices = prices?.some((price) => price.price_unit === 'day') || false;
  return hasDayPrices;
};

type MinMaxDayValues = {
  min: number;
  max: number;
  isDay: boolean;
} | undefined;

export const getMinMaxDaysForMultiDayPrices = (
  prices: Price[],
): MinMaxDayValues => {
  const multiDayPrices = prices.filter((price) => price.price_unit === 'day' || price.price_unit === 'night');
  if (multiDayPrices.length === 0) {
    return undefined;
  }

  const isDay = multiDayPrices[0].price_unit === 'day';
  const minMaxHours = getMinMaxHoursForPreferredPriceTier(multiDayPrices);
  if (!minMaxHours) {
    return undefined;
  }

  let { min } = minMaxHours;
  let { max } = minMaxHours;
  min = Math.floor(min / FULL_DAY_HOURS);
  max = Math.floor(max / FULL_DAY_HOURS);

  // is mixed pricing (has hours), so "1 day" would move to hourly options as "full day"
  const hasHourlyPrices = prices.some((price) => price.price_unit === 'hour');
  if (isDay && hasHourlyPrices && min === 1) {
    min += 1;
  }

  return {
    min,
    max,
    isDay,
  };
};

/** Ensure only night/day prices exist. */
export const isOnlyMultiDay = (prices: Price[]): boolean => {
  const hasHourPrices = prices.some((price) => price.price_unit === 'hour');
  return !hasHourPrices;
};

export const isOnlyHourly = (prices: Price[]): boolean => (
  prices.some((price) => (
    price.price_unit === 'hour'
    && !(prices.some((p) => (
      p.price_unit === 'day' || p.price_unit === 'night'
    )))))
);

// Only hourly and multiday are affected.
export const hasMixedPricingUnits = (prices: Price[]): boolean => (
  (prices.some((price) => price.price_unit === 'night') || prices.some((price) => price.price_unit === 'day'))
  && (prices.some((price) => price.price_unit === 'hour'))
);

export const calculateDaysOffset = (prices: Price[]): number => {
  const multiDayValues = getMinMaxDaysForMultiDayPrices(prices);
  if (multiDayValues) {
    return multiDayValues?.isDay
      ? (multiDayValues?.min || 0) - 1 // Day is subtracted when 'day'
      : multiDayValues?.min;
  }
  return 0;
};

export const hasOneDayAsPricingOption = (prices: Price[]): boolean => {
  const dayPrices = prices.filter((price) => price.price_unit === 'day');
  if (dayPrices.length) {
    return dayPrices.some((dayPrice) => dayPrice.min_unit_quantity === 1);
  }

  return false;
};

export const getDefaultTodayDate = (unavailableDates: string[]): Date[] => {
  let defaultDate = formatDate(addDays(todayDate(), 1));

  unavailableDates.forEach((unavailableDate) => {
    if (defaultDate === unavailableDate) {
      defaultDate = formatDate(addDays(newTZDate(defaultDate), 1));
    }
  });

  return [newTZDate(defaultDate)];
};

export const getUniqueTripListByDuration = (
  trips: InstabookAvailability[],
): number[] => {
  const uniqueTrips: InstabookAvailability[] = Object.values(
    trips.reduce((acc, currentTrip) => {
      const key = currentTrip.trip_length;
      const previousTrip = acc[key];

      let keepPrevious = false;
      if (previousTrip) {
        const renterAmount = currentTrip.transaction.amounts.renter_booking_amount || 0;
        const prevRenterAmount = previousTrip.transaction.amounts.renter_booking_amount || 0;

        if (renterAmount > prevRenterAmount) {
          keepPrevious = true;
        }
      }
      return {
        ...acc,
        [key]: keepPrevious ? previousTrip : currentTrip,
      };
    }, {}),
  );

  return uniqueTrips.map((trip) => (Number(trip.trip_length.split(':')[0]))).sort((a, b) => a - b);
};

export const convertTo12HourFormat = (time: string, locale: Locale = enUS): string => {
  const [hours, minutes] = time.split(':').map((num) => parseInt(num, 10));
  const date = new Date(0, 0, 0, hours, minutes);

  // do non-en locales use 12hour?
  return formatDate(date, TIME_12, locale);
};

export const isOnlyMultiDayWithFullDay = (prices: Price[]): boolean => (
  isOnlyMultiDay(prices) && hasOneDayAsPricingOption(prices)
);

export const isFullDayDuration = (duration: string): boolean => (
  getDurationHours(duration) === FULL_DAY_HOURS
);

export const getEstimatedRate = (
  price: number,
  duration: string, // Format: PT2H
  currency: Currency,
): string => {
  if (price === 0) return '';

  const durationValue = getDurationDigits(duration);
  const rate = Math.round(price / durationValue);

  const formattedPrice = completePriceFormatter(rate, currency.code, currency.symbol);
  return currency.code === DEFAULT_CURRENCY.code
    ? `${formattedPrice}`
    : `${currency.code} ${formattedPrice}`;
};
